import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '../../Icons';

interface ColumnProps { list: string[]; value: string; onChange: (v: string) => void; label: string; }
const PickerColumn: React.FC<ColumnProps> = ({ list, value, onChange, label }) => {
    const idx = Math.max(0, list.indexOf(value));
    const handleStep = (d: number) => list.length > 1 && onChange(list[(idx + d + list.length) % list.length]);
    return (
        <div className="flex-1 flex flex-col items-center select-none bg-white">
            <div className="text-[9px] font-black text-gray-400 text-center uppercase py-2 w-full border-b border-gray-50">{label}</div>
            <button type="button" onClick={() => handleStep(-1)} className="w-full h-[30px] flex items-center justify-center text-gray-400 hover:text-sky-600 z-20"><ChevronUpIcon className="w-5 h-5" /></button>
            <div className="flex flex-col w-full h-[120px] overflow-hidden">
                {list.length === 0 ? <div className="h-full flex items-center justify-center text-gray-300 text-xs">--</div> : [-1, 0, 1].map(o => {
                    const item = list[(idx + o + list.length) % list.length];
                    return <div key={o} onClick={() => o !== 0 && onChange(item)} className={`h-[40px] flex items-center justify-center transition-all cursor-pointer ${o === 0 ? 'text-sky-600 font-black text-xl scale-110' : 'text-gray-300 text-sm opacity-40'}`}>{item}</div>;
                })}
            </div>
            <button type="button" onClick={() => handleStep(1)} className="w-full h-[30px] flex items-center justify-center text-gray-400 hover:text-sky-600 z-20"><ChevronDownIcon className="w-5 h-5" /></button>
        </div>
    );
};

export const CustomTimePicker: React.FC<{ value: string; date: string; onChange: (v: string) => void; visitConfig: any }> = ({ value, date, onChange, visitConfig }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    
    const dayName = useMemo(() => date ? { 1: "LUNES", 2: "MARTES", 3: "MIERCOLES", 4: "JUEVES", 5: "VIERNES", 6: "SABADO", 0: "DOMINGO" }[new Date(date.replace(/-/g, '/')).getDay()] : '', [date]);
    const schedule = useMemo(() => visitConfig?.HORARIO?.[dayName] || { INICIO: 8, FIN: 17 }, [visitConfig, dayName]);

    const parts = useMemo(() => {
        const [h24, m] = (value || '08:00').split(':').map(Number);
        return { h: String(h24 % 12 || 12).padStart(2, '0'), m: String(m).padStart(2, '0'), p: h24 >= 12 ? 'p.m.' : 'a.m.' };
    }, [value]);

    const amPmOptions = useMemo(() => {
        const opts = [];
        if (schedule.INICIO < 12) opts.push('a.m.');
        if (schedule.FIN >= 12) opts.push('p.m.');
        return opts.length ? opts : ['a.m.', 'p.m.'];
    }, [schedule]);

    const hourOptions = useMemo(() => {
        const hours = [];
        const isPM = parts.p === 'p.m.';
        const rangeStart = isPM ? Math.max(12, schedule.INICIO) : schedule.INICIO;
        const rangeEnd = isPM ? schedule.FIN : Math.min(11, schedule.FIN);
        for (let i = rangeStart; i <= rangeEnd; i++) {
            const h12 = String(i % 12 || 12).padStart(2, '0');
            if (!hours.includes(h12)) hours.push(h12);
        }
        return hours.length ? hours.sort() : ['--'];
    }, [schedule, parts.p]);

    const handlePartChange = (p: 'h'|'m'|'p', v: string) => {
        const n = { ...parts, [p]: v };
        let h = parseInt(n.h === '--' ? '08' : n.h);
        if (n.p === 'p.m.' && h !== 12) h += 12;
        if (n.p === 'a.m.' && h === 12) h = 0;
        h = Math.max(schedule.INICIO, Math.min(schedule.FIN, h));
        onChange(`${String(h).padStart(2, '0')}:${n.m}`);
    };

    useEffect(() => {
        const clickOutside = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false); };
        document.addEventListener('mousedown', clickOutside);
        return () => document.removeEventListener('mousedown', clickOutside);
    }, []);

    const handleConfirm = () => {
        // Ensure the current selection is emitted even if the user didn't interact
        let h = parseInt(parts.h === '--' ? '08' : parts.h);
        if (parts.p === 'p.m.' && h !== 12) h += 12;
        if (parts.p === 'a.m.' && h === 12) h = 0;
        h = Math.max(schedule.INICIO, Math.min(schedule.FIN, h));
        onChange(`${String(h).padStart(2, '0')}:${parts.m}`);
        setIsOpen(false);
    };

    if (!date) return <div className="px-3 py-2 border border-gray-100 bg-gray-50 rounded-md text-xs text-gray-400 italic">Primero elige fecha</div>;

    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:border-sky-500 transition-colors">
                <span className={value ? 'text-gray-900 font-medium' : 'text-gray-400'}>{value ? `${parts.h}:${parts.m} ${parts.p}` : '--:-- --'}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </button>
            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-2xl z-[1001] flex flex-col overflow-hidden animate-fade-in-up">
                    <div className="flex h-[200px] relative">
                        <div className="absolute top-[88px] left-0 right-0 h-[44px] border-t-2 border-b-2 border-sky-100 bg-sky-50/30 pointer-events-none z-10"></div>
                        <PickerColumn list={hourOptions} value={parts.h} onChange={v => handlePartChange('h', v)} label="Hora" />
                        <PickerColumn list={['00','15','30','45']} value={parts.m} onChange={v => handlePartChange('m', v)} label="Min" />
                        <PickerColumn list={amPmOptions} value={parts.p} onChange={v => handlePartChange('p', v)} label="AM/PM" />
                    </div>
                    <button type="button" onClick={handleConfirm} className="w-full py-2 bg-sky-600 text-white text-[11px] font-black uppercase tracking-widest">Confirmar</button>
                </div>
            )}
        </div>
    );
};
